import type { Message, SavedChat } from "~/helpers/aiChat/chatStorage";
import type { ChatMessageResult, ChatRole, ChatSessionSummary } from "~/services/aiOrchestrator";

import { compareDesc } from "date-fns/compareDesc";
import { parseISO } from "date-fns/parseISO";

import buildToolResults from "~/helpers/aiChat/buildToolResults";
import i18n from "~/i18n/i18n";

export interface HistoryRow {
	cachedChat?: SavedChat;
	id: string;
	lastActivity: string;
	sessionId: string | null;
	title: string;
}

const FALLBACK_TITLE = "New conversation";
const DERIVED_TITLE_MAX = 60;
const DEFAULT_CLIENT_TITLE = "New Chat";
const LOCAL_ERROR_PREFIX = "Error: ";
const FOLDED_FILE_PREFIX = "[File uploaded: ";

const deriveFirstMessageTitle = (messages: Message[]): string | null => {
	const text = messages.find((message) => message.role === "user")?.content.trim();
	return text ? text.slice(0, DERIVED_TITLE_MAX) : null;
};

const resolveCachedTitle = (chat: SavedChat): string | null => {
	const stored = chat.title?.trim();
	if (stored && stored !== DEFAULT_CLIENT_TITLE) return stored;
	return deriveFirstMessageTitle(chat.messages);
};

const resolveRowTitle = (serverTitle: string | null, cachedChat: SavedChat | undefined): string =>
	serverTitle?.trim() || (cachedChat && resolveCachedTitle(cachedChat)) || FALLBACK_TITLE;

const toServerRow = (session: ChatSessionSummary, savedChats: SavedChat[]): HistoryRow => {
	const cachedChat = savedChats.find((chat) => chat.sessionId === session.id);
	return {
		cachedChat,
		id: cachedChat?.id ?? session.id,
		lastActivity: session.lastActivity,
		sessionId: session.id,
		title: resolveRowTitle(session.title, cachedChat)
	};
};

const toCachedRow = (chat: SavedChat): HistoryRow => ({
	cachedChat: chat,
	id: chat.id,
	lastActivity: chat.updatedAt,
	sessionId: chat.sessionId,
	title: resolveRowTitle(null, chat)
});

const buildHistoryRows = (serverSessions: ChatSessionSummary[] | null, savedChats: SavedChat[]): HistoryRow[] => {
	const cachedRows = savedChats.filter((chat) => serverSessions === null || chat.sessionId === null).map(toCachedRow);
	const serverRows = serverSessions?.map((session) => toServerRow(session, savedChats)) ?? [];
	return [...serverRows, ...cachedRows].sort((a, b) => compareDesc(parseISO(a.lastActivity), parseISO(b.lastActivity)));
};

const toMessageRole = (role: ChatRole): Message["role"] => (role === "USER" ? "user" : "assistant");

const resolveMessageText = (message: ChatMessageResult): string =>
	message.role === "USER" && !message.content ? i18n.t("fernieChat.sentFile", { ns: "common" }) : message.content;

const toServerAnswerFields = (message: ChatMessageResult): Pick<Message, "rating" | "serverId"> =>
	message.role === "ASSISTANT" ? { rating: message.rating ?? undefined, serverId: message.id } : {};

const mapServerMessages = (serverMessages: ChatMessageResult[]): Message[] =>
	serverMessages.map((message) => ({
		content: resolveMessageText(message),
		role: toMessageRole(message.role),
		toolResults: buildToolResults(message.toolCalls),
		...toServerAnswerFields(message)
	}));

const holdsUserMessage = (cached: Message, serverContent: string): boolean =>
	serverContent === cached.content ||
	serverContent.startsWith(`${cached.content}\n\n`) ||
	(cached.fileName !== undefined && (serverContent === "" || serverContent.startsWith(FOLDED_FILE_PREFIX)));

const holdsServerMessage = (cached: Message, serverMessage: ChatMessageResult): boolean => {
	if (cached.role !== toMessageRole(serverMessage.role)) return false;
	if (cached.role === "user") return holdsUserMessage(cached, serverMessage.content);
	return !cached.content.startsWith(LOCAL_ERROR_PREFIX);
};

const holdsEveryServerMessage = (cachedMessages: Message[], serverMessages: ChatMessageResult[]): boolean => {
	let heldCount = 0;
	for (const cached of cachedMessages) {
		const nextServerMessage = serverMessages[heldCount];
		if (nextServerMessage && holdsServerMessage(cached, nextServerMessage)) heldCount++;
	}
	return heldCount === serverMessages.length;
};

const withServerAnswerFields = (cachedMessages: Message[], serverMessages: ChatMessageResult[]): Message[] => {
	let heldCount = 0;
	return cachedMessages.map((cached) => {
		const nextServerMessage = serverMessages[heldCount];
		if (!nextServerMessage || !holdsServerMessage(cached, nextServerMessage)) return cached;
		heldCount++;
		return { ...cached, ...toServerAnswerFields(nextServerMessage) };
	});
};

const chatHistory = {
	buildHistoryRows,
	holdsEveryServerMessage,
	mapServerMessages,
	withServerAnswerFields
};

export default chatHistory;
